import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

function getHeaders() {
  const user = JSON.parse(localStorage.getItem("user"));
  return { Authorization: "Bearer " + user?.Token, Accept: "*/*" };
}

const API = process.env.REACT_APP_API_URL;

// ── Thunks ─────────────────────────────────────────────────────────

export const getApprovedQoForJr = createAsyncThunk("jrV2/approvedQo", async (_, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "getApprovedQoForJr", { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const getJrTemplate = createAsyncThunk("jrV2/template", async (arg, { rejectWithValue }) => {
  // arg bisa string (qoNo) atau objek { qoNo, jrType }. jrType "GS" = General Service.
  const qoNo = typeof arg === "string" ? arg : arg?.qoNo;
  const jrType = typeof arg === "string" ? "" : (arg?.jrType ?? "");
  try {
    const res = await axios.get(API + "getJrTemplate", { headers: getHeaders(), params: { qoNo, jrType } });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

// CQ aktif milik customer (turunan Master Customer) untuk dropdown form JR.
export const getJrCqOptions = createAsyncThunk("jrV2/cqOptions", async (klienID, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "getJrCqOptions", { headers: getHeaders(), params: { klienID } });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const getJrDetail = createAsyncThunk("jrV2/detail", async (jrID, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "getJrDetailV2", { headers: getHeaders(), params: { jrID } });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const getJrList = createAsyncThunk("jrV2/list", async ({ search = "", QoNo = "", Status = "", JrType = "" } = {}, { rejectWithValue }) => {
  try {
    const fd = new FormData();
    fd.append("search", search);
    fd.append("QoNo", QoNo);
    fd.append("Status", Status);
    fd.append("JrType", JrType);
    const res = await axios.post(API + "getJrListV2", fd, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

// Detail entri Activity (harian) per JrLine — General Service
export const getJrLineDetail = createAsyncThunk("jrV2/getLineDetail", async (jrLineID, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "getJrLineDetailV2", { headers: getHeaders(), params: { jrLineID } });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

// Kamar × tanggal yang sudah terpakai entri akomodasi LAIN — dipakai menyaring
// dropdown Room pada baris item "Room All".
export const getRoomOccupancy = createAsyncThunk("jrV2/roomOccupancy", async (jrLineID, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "getRoomOccupancyV2", { headers: getHeaders(), params: { jrLineID } });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const saveJrLineDetail = createAsyncThunk("jrV2/saveLineDetail", async ({ JrID, JrLineID, Entries }, { rejectWithValue }) => {
  try {
    const res = await axios.post(API + "saveJrLineDetailV2", { JrID, JrLineID, Entries }, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

// Quotation yang layak dibuatkan JR General Service pada periode (YYYY-MM).
export const getGsQoOptions = createAsyncThunk("jrV2/gsQoOptions", async (period, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "getGsQoOptions", { headers: getHeaders(), params: { period } });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

// Generate SATU JR General Service (Draft) untuk QO + CQ + periode (YYYY-MM)
export const generateGsJr = createAsyncThunk("jrV2/generateGs", async ({ period, qoNo, cqID }, { rejectWithValue }) => {
  try {
    const fd = new FormData();
    fd.append("Period", period);
    fd.append("QoNo", qoNo);
    fd.append("CQID", cqID || 0);
    const res = await axios.post(API + "generateGsJrV2", fd, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const insertJr = createAsyncThunk("jrV2/insert", async (payload, { rejectWithValue }) => {
  try {
    const res = await axios.post(API + "insertJrV2", payload, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const updateJr = createAsyncThunk("jrV2/update", async (payload, { rejectWithValue }) => {
  try {
    const res = await axios.post(API + "updateJrV2", payload, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const updateJrQty = createAsyncThunk("jrV2/updateQty", async ({ JrID, Items }, { rejectWithValue }) => {
  try {
    const res = await axios.post(API + "updateJrQtyV2", { JrID, Items }, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const submitJr = createAsyncThunk("jrV2/submit", async (jrID, { rejectWithValue }) => {
  try {
    const fd = new FormData();
    fd.append("JrID", jrID);
    const res = await axios.post(API + "submitJrV2", fd, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const sendJrToClient = createAsyncThunk("jrV2/send", async (jrID, { rejectWithValue }) => {
  try {
    const fd = new FormData();
    fd.append("JrID", jrID);
    const res = await axios.post(API + "sendJrToClientV2", fd, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

// Approval internal RKS: 100 → 150 → (setuju) 199 / (tolak) kembali ke 100.
export const kirimJrApproval = createAsyncThunk("jrV2/kirimApproval", async (jrID, { rejectWithValue }) => {
  try {
    const fd = new FormData();
    fd.append("JrID", jrID);
    const res = await axios.post(API + "kirimJrApprovalV2", fd, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const aksiJrApproval = createAsyncThunk("jrV2/aksiApproval", async ({ JrID, Aksi, Alasan = "" }, { rejectWithValue }) => {
  try {
    const fd = new FormData();
    fd.append("JrID", JrID);
    fd.append("Aksi", Aksi);          // "approve" | "reject"
    fd.append("Alasan", Alasan);      // wajib saat menolak
    const res = await axios.post(API + "aksiJrApprovalV2", fd, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const approveJr = createAsyncThunk("jrV2/approve", async (jrID, { rejectWithValue }) => {
  try {
    const fd = new FormData();
    fd.append("JrID", jrID);
    const res = await axios.post(API + "approveJrV2", fd, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const rejectJrItems = createAsyncThunk("jrV2/rejectItems", async ({ JrID, Items }, { rejectWithValue }) => {
  try {
    const res = await axios.post(API + "rejectJrItemsV2", { JrID, Items }, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

// Semua versi JR (arsip recall + versi berjalan) untuk halaman perbandingan.
export const getJrVersions = createAsyncThunk("jrV2/versions", async (jrID, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "getJrVersionsV2", { headers: getHeaders(), params: { jrID } });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

// Recall JR Approved → Revisi JR (versi naik); body { JrID, Reason }.
export const recallJr = createAsyncThunk("jrV2/recall", async ({ JrID, Reason }, { rejectWithValue }) => {
  try {
    const res = await axios.post(API + "recallJrV2", { JrID, Reason }, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const reviseJrActivity = createAsyncThunk("jrV2/reviseActivity", async (JrID, { rejectWithValue }) => {
  try {
    const res = await axios.post(API + "reviseJrActivityV2", { JrID }, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const getJrManifest = createAsyncThunk("jrV2/getManifest", async (jrID, { rejectWithValue }) => {
  try {
    const res = await axios.get(API + "getJrManifestV2", { headers: getHeaders(), params: { jrID } });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

export const saveJrManifest = createAsyncThunk("jrV2/saveManifest", async (payload, { rejectWithValue }) => {
  try {
    const res = await axios.post(API + "saveJrManifestV2", payload, { headers: getHeaders() });
    return res.data;
  } catch (e) { return rejectWithValue(e.response?.data); }
});

// ── Slice ──────────────────────────────────────────────────────────

const initialState = {
  approvedQo: [],
  template: [],
  detail: null,
  list: [],
  manifest: null,        // { Header, Manifest }
  isManifestLoading: false,
  isManifestSaveSuccess: false,
  isLoading: false,
  isTemplateLoading: false,
  isDetailLoading: false,
  isSaving: false,
  isSaveSuccess: false,
  isGenerating: false,
  generateResult: null,   // { Period, Created, Skipped, JrIDs }
  error: null,
};

const jrV2Slice = createSlice({
  name: "jrV2",
  initialState,
  reducers: {
    resetSaveStatus(state) { state.isSaveSuccess = false; state.error = null; },
    clearGenerateResult(state) { state.generateResult = null; },
    clearDetail(state) { state.detail = null; },
    clearTemplate(state) { state.template = []; },
    clearManifest(state) { state.manifest = null; state.isManifestSaveSuccess = false; },
    resetManifestSave(state) { state.isManifestSaveSuccess = false; state.error = null; },
  },
  extraReducers: (b) => {
    b.addCase(getApprovedQoForJr.fulfilled, (s, a) => { s.approvedQo = a.payload?.data ?? []; });

    b.addCase(getJrTemplate.pending,   (s) => { s.isTemplateLoading = true; });
    b.addCase(getJrTemplate.fulfilled, (s, a) => { s.isTemplateLoading = false; s.template = a.payload?.data ?? []; });
    b.addCase(getJrTemplate.rejected,  (s) => { s.isTemplateLoading = false; });

    b.addCase(getJrDetail.pending,   (s) => { s.isDetailLoading = true; });
    b.addCase(getJrDetail.fulfilled, (s, a) => { s.isDetailLoading = false; s.detail = a.payload?.data ?? null; });
    b.addCase(getJrDetail.rejected,  (s, a) => { s.isDetailLoading = false; s.error = a.payload?.message; });

    b.addCase(getJrList.pending,   (s) => { s.isLoading = true; });
    b.addCase(getJrList.fulfilled, (s, a) => { s.isLoading = false; s.list = a.payload?.data ?? []; });
    b.addCase(getJrList.rejected,  (s) => { s.isLoading = false; });

    b.addCase(generateGsJr.pending,   (s) => { s.isGenerating = true; s.generateResult = null; });
    b.addCase(generateGsJr.fulfilled, (s, a) => { s.isGenerating = false; s.generateResult = a.payload?.data ?? null; });
    b.addCase(generateGsJr.rejected,  (s, a) => { s.isGenerating = false; s.error = a.payload?.message; });

    b.addCase(insertJr.pending,   (s) => { s.isSaving = true; s.isSaveSuccess = false; });
    b.addCase(insertJr.fulfilled, (s, a) => { s.isSaving = false; s.isSaveSuccess = true; s.detail = a.payload?.data; });
    b.addCase(insertJr.rejected,  (s, a) => { s.isSaving = false; s.error = a.payload?.message; });

    b.addCase(updateJr.pending,   (s) => { s.isSaving = true; s.isSaveSuccess = false; });
    b.addCase(updateJr.fulfilled, (s, a) => { s.isSaving = false; s.isSaveSuccess = true; s.detail = a.payload?.data; });
    b.addCase(updateJr.rejected,  (s, a) => { s.isSaving = false; s.error = a.payload?.message; });

    b.addCase(getJrManifest.pending,   (s) => { s.isManifestLoading = true; });
    b.addCase(getJrManifest.fulfilled, (s, a) => { s.isManifestLoading = false; s.manifest = a.payload?.data ?? null; });
    b.addCase(getJrManifest.rejected,  (s, a) => { s.isManifestLoading = false; s.error = a.payload?.message; });

    b.addCase(saveJrManifest.pending,   (s) => { s.isSaving = true; s.isManifestSaveSuccess = false; });
    b.addCase(saveJrManifest.fulfilled, (s) => { s.isSaving = false; s.isManifestSaveSuccess = true; });
    b.addCase(saveJrManifest.rejected,  (s, a) => { s.isSaving = false; s.error = a.payload?.message; });
  },
});

export const { resetSaveStatus, clearGenerateResult, clearDetail, clearTemplate, clearManifest, resetManifestSave } = jrV2Slice.actions;
export const jrV2Selector = (state) => state.jrV2;
export default jrV2Slice.reducer;
